/**
 * The weekly progress series — `GET /blueprints/:id/weeks` (T7.15, read by T7.26).
 *
 * The wire shape is the API's `weeksResponse` (apps/api/src/routes/schemas.ts).
 * Three of its fields carry a distinction this module must not blur, and the
 * types here keep them apart on purpose:
 *
 *   `overallPct: null`   nothing had been published by that week. It is BLANK,
 *                        not zero — nothing was reported, which is not the same
 *                        as no progress (design.md § 10a, 7.4). Never coalesce
 *                        this to 0, and never render a figure for it.
 *   `carriedForward`     the week has no version of its own and is showing the
 *                        previous week's. The figure is real; the week it was
 *                        published in is not this one, and the readout says so.
 *   `effectiveAt`        the week the version COUNTS as, which is what the
 *                        slider reads. `publishedAt` — when it really happened —
 *                        belongs to the audit trail and is deliberately absent
 *                        from this payload.
 *
 * No percentage is computed here. Every figure arrives already computed by the
 * shared `buildModel`, per version, with that version's own state scale.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { blueprintKeys } from './blueprints';

export interface WeekRoomFigure {
  RoomID: string;
  RoomName: string;
  pct: number;
}

export interface WeekSeriesRow {
  /** Monday, `YYYY-MM-DD`. */
  start: string;
  /** Friday of the same week, matching the existing week highlighter's labels. */
  end: string;
  versionId: string | null;
  versionNo: number | null;
  /** The version's release note, which the API calls `label`. */
  label: string | null;
  effectiveAt: string | null;
  carriedForward: boolean;
  /** Null means blank — see the header note. */
  overallPct: number | null;
  rooms: WeekRoomFigure[];
}

export interface WeekSeries {
  weeks: WeekSeriesRow[];
  /** Index of the week containing today, or null when today is outside the range. */
  currentWeekIndex: number | null;
}

export const weekSeriesKey = (blueprintId: string) =>
  [...blueprintKeys.detail(blueprintId), 'weeks'] as const;

/**
 * Available to every role that can open the blueprint: every row is a published
 * version, so there is no draft here for D-2 to withhold.
 */
export function useWeekSeries(blueprintId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: weekSeriesKey(blueprintId ?? ''),
    queryFn: ({ signal }) => api.get<WeekSeries>(`/blueprints/${blueprintId}/weeks`, signal),
    enabled: Boolean(blueprintId) && enabled,
  });
}

/* ------------------------------------------------- reading the series (T9.x)
 *
 * These three live here, beside the type, because the slider and the route both
 * need the same answers and a second copy of any of them would be a second
 * opinion about which week is which.
 */

/**
 * The series truncated at the CURRENT week.
 *
 * The API deliberately returns the whole plan span — that is right for other
 * consumers and is not changed — but the slider is a record of what was
 * reported, and a week that has not happened cannot have had a report. Walking
 * into next February would say "no version was published in this week" about
 * twenty weeks where none was ever due, and would carry the latest figure
 * forward as a progress number for weeks that have not occurred: the same error
 * as showing 0% before the first version.
 *
 * The current week itself stays: it is in progress and may already hold a
 * version. A plan entirely in the past keeps every week it had.
 *
 * The series is ascending, so this is a PREFIX — which is why
 * `currentWeekIndex` still indexes correctly into the result.
 */
export function weeksToDate(series: WeekSeries, thisMonday: string): WeekSeriesRow[] {
  return series.weeks.filter((w) => w.start <= thisMonday);
}

/** The index of a week by its Monday, or -1. The slider's URL parameter is that Monday. */
export function indexOfWeek(weeks: readonly WeekSeriesRow[], start: string | null): number {
  if (!start) return -1;
  return weeks.findIndex((w) => w.start === start);
}

/**
 * The week a version was published INTO — the one that owns it rather than one
 * carrying it forward. Used so that choosing a version in the dropdown leaves
 * the slider on that version's week instead of the two fighting (T9.6).
 */
export function indexOfVersionWeek(
  weeks: readonly WeekSeriesRow[],
  versionId: string | null,
): number {
  if (!versionId) return -1;
  return weeks.findIndex((w) => !w.carriedForward && w.versionId === versionId);
}
